import { useTranslation } from "react-i18next";
import { BaseActivity } from "../../../model/activity/BaseActivity";
import { useAppSelector } from "../../../redux/store";
import { permissionSliceSelectors } from "../../../redux/permission/permission.selector";
import { ActivityEntityKind } from "../../../model/activity/types/ActivityEntityKind.enum";
import { ActivityAction } from "../../../model/activity/types/ActivityAction.enum";
import { ActivityMetaMap } from "../../../model/activity/types/ActivityMetaKeysMap";
import { ActivityMetaKeys } from "../../../model/activity/types/ActivityMetaKeys.enum";
import { kanbanSliceSelectors } from "../../../redux/kanban/kanban.selector";
import {
  getTranslatedKanbanColumnName,
  getTranslatedKanbanName,
} from "../../../routes/kanban/KanbanUtils";
import { KanbanCard } from "../../../model/kanban/KanbanCard";
import { KanbanClosingReason } from "../../../model/kanban/types/KanbanClosingReason";
import groupBy from "lodash/groupBy";
import {
  formatDisplayValue,
  getEntryActivityInput,
  isAutomationRuleActivity,
  isBlankValue,
  translateAutomationActionType,
} from "./activityUtils";
import { User } from "../../../model/user/User";
import capitalize from "lodash/capitalize";
import { userSliceSelectors } from "../../../redux/user/user.selector";
import { useDrawersContext } from "../../../routes/tasks/drawers-context";
import { InputTypes } from "../../../model/form/types/InputTypes.enum";
import { isCustomDateInput } from "../../../utils/isCustomDateInput";
import { standardDate } from "../../../utils/Date";

export function useGetDiffPairRenderByKind(
  obj: Record<string, { from?: any; to?: any }>,
  activity: BaseActivity,
  config: { user: User },
) {
  const { t } = useTranslation();
  const members = useAppSelector(permissionSliceSelectors.selectMembers);
  const kanbansMeta = useAppSelector(kanbanSliceSelectors.selectKanbansMeta);
  const user = useAppSelector(userSliceSelectors.selectLoggedInUser)!;
  const { entry } = useDrawersContext();

  const key = Object.keys(obj)[0];
  const node = obj[key];

  const { kind, action } = activity;

  switch (kind) {
    case ActivityEntityKind.ENTRY: {
      if (action === ActivityAction.CONVERT) {
        const _node =
          node as ActivityMetaMap[ActivityMetaKeys.CONVERT_TO]["kind"];
        const from = _node?.from as string;
        const to = _node?.to as string;

        return {
          from: from ? t(`common.${from?.toLowerCase()}`) : "",
          to: to ? t(`common.${to?.toLowerCase()}`) : "",
        };
      }

      if (action === ActivityAction.ASSIGN) {
        const { assigneeIds } =
          node as ActivityMetaMap[ActivityMetaKeys.NEW_ASSIGNEES];

        return {
          from: `(${t("common.noOne")})`,
          to:
            members
              .filter((m) => assigneeIds.includes(m._id))
              ?.map((m) => m.name)
              .join(", ") || `(${t("common.noOne")})`,
        };
      }

      if (action === ActivityAction.UPDATE) {
        const _node = node as ActivityMetaMap[ActivityMetaKeys.PATCH];
        if (key === "kind") {
          return {
            from: t(`common.${_node?.from?.toLowerCase()}`),
            to: t(`common.${_node?.to?.toLowerCase()}`),
          };
        }

        const input = getEntryActivityInput(activity, entry, key);

        if (input?.type?.toLowerCase() === InputTypes.DATE.toLowerCase()) {
          const isBooking = isCustomDateInput(
            input.type,
            input.dateInputConfig,
          );
          const formatDate = (v: unknown) =>
            isBlankValue(v)
              ? ""
              : standardDate(
                  v as string,
                  config.user,
                  isBooking,
                  false,
                  isBooking ? input.dateInputConfig?.timezone : undefined,
                );

          return { from: formatDate(_node?.from), to: formatDate(_node?.to) };
        }

        if (input?.type?.toLowerCase() === InputTypes.CHECKBOX.toLowerCase()) {
          const formatCheckbox = (v: unknown) =>
            isBlankValue(v) ? "" : t(v === true ? "common.yes" : "common.no");

          return {
            from: formatCheckbox(_node?.from),
            to: formatCheckbox(_node?.to),
          };
        }

        return {
          from: String(_node?.from ?? ""),
          to: String(_node?.to ?? ""),
        };
      }

      break;
    }

    case ActivityEntityKind.TASK: {
      if (action === ActivityAction.INCREMENT_ATTEMPT) {
        const _node = node as ActivityMetaMap[ActivityMetaKeys.PATCH];

        return {
          from: String(_node?.from || 0),
          to: String(_node?.to || 0),
        };
      }

      if (action === ActivityAction.UPDATE) {
        if (key === ActivityMetaKeys.NEW_ASSIGNEES) {
          const { assigneeIds } =
            node as ActivityMetaMap[ActivityMetaKeys.NEW_ASSIGNEES];

          return {
            from:
              members
                .filter((m) => assigneeIds.includes(m._id))
                ?.map((m) => m.name)
                .join(", ") || `(${t("common.noOne")})`,
          };
        }

        if (key === "assigneeIds") {
          const _node = node as ActivityMetaMap[ActivityMetaKeys.PATCH];

          const fromAssigneeIds = _node?.from as string[];
          const toAssigneeIds = _node?.to as string[];

          return {
            from:
              members
                .filter((m) => fromAssigneeIds?.includes(m._id))
                ?.map((m) => m.name)
                .join(", ") || `(${t("common.noOne")})`,
            to:
              members
                .filter((m) => toAssigneeIds?.includes(m._id))
                ?.map((m) => m.name)
                .join(", ") || `(${t("common.noOne")})`,
          };
        }

        if (key === "done") {
          return {
            from: node?.from ? t("common.yes") : t("common.no"),
            to: node?.to ? t("common.yes") : t("common.no"),
          };
        }

        if (key === "doneBy") {
          return {
            from: node?.from,
            to:
              [...members, user].find((m) => m._id === node?.to)?.name ||
              node?.to,
          };
        }

        return {
          from: formatDisplayValue(node?.from, config.user),
          to: formatDisplayValue(node?.to, config.user),
        };
      }

      break;
    }

    case ActivityEntityKind.KANBAN: {
      if (key === "closingReasons") {
        const kanban = kanbansMeta?.find((k) => k._id === activity.resourceId);
        const formatReasons = (reasons?: KanbanClosingReason[]) =>
          Object.entries(groupBy(reasons || [], "stageType"))
            .map(([stageType, list]) => {
              const column = kanban?.columns?.find(
                (c) => c.stageType === stageType,
              );
              const name =
                getTranslatedKanbanColumnName(column?.name, kanban?.template) ||
                stageType;

              return `${name}: ${list.map((r) => r.label).join(", ")}`;
            })
            .join(" · ");

        return {
          from: formatReasons(node?.from),
          to: formatReasons(node?.to),
        };
      }

      break;
    }

    case ActivityEntityKind.KANBAN_COLUMN: {
      if (isAutomationRuleActivity(activity)) {
        if (key === "actionType") {
          // A move logs this stated plainly (no `to`) just to say which rule
          // moved - the type itself didn't change.
          return node?.to === undefined
            ? { from: translateAutomationActionType(t, node?.from) }
            : {
                from: translateAutomationActionType(t, node?.from),
                to: translateAutomationActionType(t, node?.to),
              };
        }

        if (key === "column") {
          const template = kanbansMeta?.find(
            (k) => k._id === activity.meta?.shared?.parentId,
          )?.template;

          return {
            from: getTranslatedKanbanColumnName(node?.from, template),
            to: getTranslatedKanbanColumnName(node?.to, template),
          };
        }

        return {
          from: formatDisplayValue(node?.from, config.user),
          to: formatDisplayValue(node?.to, config.user),
        };
      }

      // Default column names are stored in English and translated for display,
      // so the timeline has to translate them the same way the board does.
      if (key === "name") {
        const kanban = kanbansMeta?.find((k) =>
          k.columns?.some((c) => c._id === activity.resourceId),
        );

        return {
          from: getTranslatedKanbanColumnName(node?.from, kanban?.template),
          to: getTranslatedKanbanColumnName(node?.to, kanban?.template),
        };
      }

      break;
    }

    case ActivityEntityKind.KANBAN_CARD: {
      if (action === ActivityAction.MOVE_BETWEEN_KANBANS) {
        const _node = node as ActivityMetaMap[ActivityMetaKeys.CHANGE_KANBAN];

        const sourceKanban = kanbansMeta?.find(
          (k) => k._id === _node?.fromKanbanId,
        );
        const sourceKanbanDisplayName = getTranslatedKanbanName(
          sourceKanban?.name || "",
        );
        const sourceColumn = sourceKanban?.columns?.find(
          (c) => c._id === _node?.fromKanbanColumnId,
        );
        const sourceColumnDisplayName = getTranslatedKanbanColumnName(
          sourceColumn?.name || "",
          sourceKanban?.template,
        );

        const newKanban = kanbansMeta?.find((k) => k._id === _node.toKanbanId);

        const newKanbanDisplayName = getTranslatedKanbanName(
          newKanban?.name || "",
        );
        const newColumn = newKanban?.columns?.find(
          (c) => c._id === _node?.toKanbanColumnId,
        );
        const newColumnDisplayName = getTranslatedKanbanColumnName(
          newColumn?.name || "",
          newKanban?.template,
        );

        return {
          from: `${sourceKanbanDisplayName} / ${sourceColumnDisplayName}`,
          to: `${newKanbanDisplayName} / ${newColumnDisplayName}`,
        };
      }

      if (action === ActivityAction.UPDATE) {
        const _node = node as ActivityMetaMap[ActivityMetaKeys.PATCH];

        if (key === "customFields") {
          return {
            from:
              (_node?.from as KanbanCard["customFields"])
                ?.map((f, i) => `${f?.label || i}: ${f?.value}`)
                .join(", ") || "",
            to:
              (_node?.to as KanbanCard["customFields"])
                ?.map((f, i) => `${f?.label || i}: ${f?.value}`)
                .join(", ") || "",
          };
        }

        if (key === "parentId") {
          const findColumnName = (id: string) => {
            const column = kanbansMeta
              ?.flatMap((k) => k.columns)
              ?.find((c) => c?._id === id);

            const kanban = kanbansMeta?.find((k) =>
              k.columns?.some((col) => col._id === id),
            );

            return getTranslatedKanbanColumnName(
              column?.name,
              kanban?.template,
            );
          };

          return {
            from: findColumnName(String(_node?.from)),
            to: findColumnName(String(_node?.to)),
          };
        }

        return {
          from: formatDisplayValue(_node?.from, config.user),
          to: formatDisplayValue(_node?.to, config.user),
        };
      }

      break;
    }

    case ActivityEntityKind.FORM: {
      if (key === "autoAssignAssigneeIds") {
        const _node = node as ActivityMetaMap[ActivityMetaKeys.PATCH];

        const nameList = (ids: unknown) =>
          ((ids as string[]) || [])
            .map((id) => members.find((m) => m._id === id)?.name)
            .filter(Boolean)
            .join(", ") || `(${t("common.noOne")})`;

        return {
          from: nameList(_node?.from),
          to: nameList(_node?.to),
        };
      }

      break;
    }

    case ActivityEntityKind.MODULE: {
      if (action === ActivityAction.UPDATE) {
        const _node = node as ActivityMetaMap[ActivityMetaKeys.PATCH];

        if (key === "icon") {
          return {
            from: _node?.from?.name
              ? capitalize(_node?.from?.name)
              : t("common.noIcon"),
            to: _node?.to?.name
              ? capitalize(_node?.to?.name)
              : t("common.noIcon"),
          };
        }

        return {
          from: formatDisplayValue(_node?.from, config.user),
          to: formatDisplayValue(_node?.to, config.user),
        };
      }

      break;
    }

    default: {
    }
  }

  // Every kind and action must end up with something to show. FORM activities in
  // particular had no branch at all, so their rows rendered a label above an
  // empty line.
  return {
    from: formatDisplayValue(node?.from, config.user),
    to: formatDisplayValue(node?.to, config.user),
  };
}
